import { useContext } from 'react'
import { Trans, useLingui } from '@lingui/react/macro'
import { price, PricingType } from '@guestgoodie/pricing'
import { GraphQL } from '@guestgoodie/graphql'

import * as Form from '@/features/form'
import Input from '@/components/ui/Input'
import { TabSwitch } from '@/components/ui/TabSwitch'
import { FormProps } from '../hooks/useSteps'
import { useIntervals } from '../hooks/useIntervals'
import { priceFormatter } from '../utils/helpers'
import { BillingContext } from '../hooks/useBillingData'
import { RoomsFormData } from '../hooks/useBillingForm'

type FormData = {
  rooms: number
  interval: GraphQL.BillingInterval
}

export function RoomsForm({ ref }: FormProps) {
  const { t } = useLingui()
  const { billing, update } = useContext(BillingContext)
  const intervalOptions = useIntervals()

  return (
    <Form.Root<RoomsFormData>
      onSubmit={update}
      formRef={ref}
      values={billing as RoomsFormData}
      defaultValues={
        (billing as RoomsFormData) ?? {
          interval: GraphQL.BillingInterval.Yearly,
          rooms: 150
        }
      }
    >
      <div className="flex flex-col gap-6 p-2.5">
        <div className="flex flex-row items-center gap-3 justify-center">
          <Form.Data<FormData>>
            {({ rooms, interval }) => {
              const { totalPrice, unitPrice } = price(
                interval as unknown as PricingType,
                rooms ? rooms : 150
              )

              return (
                <h3 className="flex flex-row items-center gap-2 text-heading2 text-text">
                  <span>
                    <Trans>Price in netto per month:</Trans>
                  </span>
                  <span>{rooms}</span>
                  <span>x</span>
                  <span>{priceFormatter.format(unitPrice / 100)}</span>
                  <span>=</span>
                  <span>{priceFormatter.format(totalPrice / 100)}</span>
                </h3>
              )
            }}
          </Form.Data>
        </div>
        <Form.Field
          name="interval"
          rules={{
            required: t`This field is required`
          }}
        >
          {({ renderProps: props }) => (
            <TabSwitch options={intervalOptions} autoFocus {...props} />
          )}
        </Form.Field>
        <Form.Field
          name="rooms"
          label={t`How many Rooms do you have?`}
          rules={{
            required: t`This field is required`
          }}
        >
          {({ renderProps: { onChange, ...props } }) => (
            <Input
              type="number"
              min={1}
              onChange={event => {
                const value = parseInt(event.target.value)

                onChange(isNaN(value) ? null : value)
              }}
              {...props}
            />
          )}
        </Form.Field>
      </div>
    </Form.Root>
  )
}
